<!--
  Single multi-concern template shared by FACE and HAIR.
  Class-specific differences are driven by the `hairMultiConcernUI` flag:
  - primary header top-margin, body bottom-padding
  - primary concern checkbox size + checked marker (cs-icon vs svg)
  - `tw-capitalize` on concern names
  - secondary-concerns heading copy (skin vs hair)
  - which CTA shows the loader (see `isNoConcernSelected`)
-->
<div class="tw-flex tw-flex-col tw-h-full tw-bg-teal-200">
  <!-- Header section -->
  @if (showSecondaryConcerns) {
    <!-- Secondary concerns header -->
    @if (isProfileBreakdownEnabled) {
      <div class="tw-flex-none tw-pb-4 tw-pl-6 tw-pt-16 tw-animate-onboarding_page_enter">
        <div id="backButton"
          class="tw-h-6 tw-w-6"
          role="button"
          (click)="handleBackToPrimaryConcerns()">
          <cs-icon class="tw-h-6 tw-w-6 tw-text-gray-800"
            [iconName]="'arrow_back'"></cs-icon>
        </div>
      </div>
    } @else {
      <div class="tw-relative tw-min-h-14 tw-max-h-14 tw-h-14">
        <div id="backButton"
          class="tw-absolute tw-ml-6 tw-mt-6 tw-h-10 tw-w-10 tw-flex tw-items-center tw-justify-center tw-bg-gray-800 tw-rounded-xl tw-z-20"
          (click)="handleBackToPrimaryConcerns()">
          <cs-icon class="tw-h-6 tw-w-6 tw-text-white"
            [iconName]="'arrow_back'"></cs-icon>
        </div>
      </div>
    }
  } @else if (isProfileBreakdownEnabled) {
    <div class="tw-flex-none tw-animate-onboarding_page_enter">
      <div id="backButton"
        class="tw-ml-6 tw-mt-16 tw-h-6 tw-w-6"
        role="button"
        (click)="handleBackClick()">
        <cs-icon class="tw-h-6 tw-w-6 tw-text-gray-800"
          [iconName]="'arrow_back'"></cs-icon>
      </div>
      <h1 class="tw-font-lexend tw-font-bold tw-text-700 tw-text-gray-800 tw-mt-8 tw-mb-4 tw-mx-6"
        i18n="@@chooseYourMostImportantConcern">Choose your most important concern</h1>
    </div>
  } @else {
    <!-- Primary concerns header -->
    <div class="tw-relative"
      [ngClass]="hairMultiConcernUI ? 'tw-mt-10' : 'tw-mt-2'">
      <div id="backButton"
        class="tw-ml-6 tw-mt-6 tw-h-10 tw-w-10 tw-flex tw-items-center tw-justify-center tw-bg-gray-800 tw-rounded-xl"
        (click)="handleBackClick()">
        <cs-icon class="tw-h-6 tw-w-6 tw-text-white"
          [iconName]="'arrow_back'"></cs-icon>
      </div>
      <h1 class="tw-font-lexend tw-font-bold tw-text-700 tw-text-gray-800 tw-mt-8 tw-mb-6 tw-ml-6 tw-max-w-[55%]"
        i18n="@@chooseYourMostImportantConcern">Choose your most important concern</h1>
      <img class="tw-absolute tw-h-[200px] tw-right-[-16px] tw-bottom-[-40px]"
        src="/assets/images/dr-charu.png"
        alt="Dr Charu" />
    </div>
  }
  <!-- Body section -->
  <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-white tw-px-6 tw-pt-6 tw-rounded-tr-5 tw-rounded-tl-5 tw-z-10"
    [class.tw-pb-4]="hairMultiConcernUI">
    @if (ui.loading) {
      <loading class="tw-justify-center tw-items-start"></loading>
    } @else {
      <!-- Switch between primary concerns list and secondary concerns list -->
      @if (showSecondaryConcerns) {
        <!-- Secondary concerns list -->
        <div class="tw-flex-none"
          [ngClass]="secondaryConcernsHeadingClass">
          @if (hairMultiConcernUI) {
            <h1 class="tw-font-lexend tw-font-bold tw-text-700 tw-text-gray-800"
              i18n="@@doYouHaveAnyOtherHairConcerns">Do you have any<br /> other hair concerns?</h1>
          } @else {
            <h1 class="tw-font-lexend tw-font-bold tw-text-700 tw-text-gray-800"
              i18n="@@doYouHaveAnyOtherSkinConcerns">Do you have any<br /> other skin concerns?</h1>
          }
          <span class="tw-font-body tw-inline-block tw-text-300 tw-text-gray-800 tw-mt-1"
            i18n="@@selectUpto3">(Select upto 3)</span>
        </div>
        @if (selectedConcernsDetails?.[0]?.concernsList?.length) {
          <div class="tw-min-h-0 tw-flex-1 tw-overflow-x-hidden tw-overflow-y-auto"
            [ngClass]="pageEnterAnimationClass">
            <ul class="tw-flex tw-flex-wrap tw-gap-4">
              @for (item of selectedConcernsDetails[0].concernsList; track item; let i = $index) {
                <li class="tw-box-border tw-flex tw-justify-center tw-items-center tw-gap-2 tw-px-4 tw-py-[9px] tw-border tw-border-solid tw-border-gray-400 tw-rounded-6 tw-transition-colors tw-duration-200"
                  [ngClass]='{"tw-opacity-30": !selectedSecondaryConcerns[item.displayName] && isOpacityConditionMet, "tw-bg-teal-100 tw-border-teal-400": selectedSecondaryConcerns[item.displayName]}'
                  (click)="addRemoveSecondaryConcern(item)">
                  @if (!selectedSecondaryConcerns[item.displayName]) {
                    <div class="tw-w-4 tw-h-4 tw-bg-gray-50 tw-border tw-border-solid tw-border-gray-400 tw-rounded-[50%] tw-shrink-0">
                    </div>
                  }
                  @if (selectedSecondaryConcerns[item.displayName]) {
                    <img class="tw-w-4 tw-h-4 tw-shrink-0 tw-animate-tick_pop"
                      src="/assets/images/secondary-concern-selected.svg"
                      alt="selected" />
                  }
                  <span class="tw-font-lexend tw-text-300 tw-text-gray-800"
                    [class.tw-capitalize]="hairMultiConcernUI">{{item.displayName}}</span>
                </li>
              }
            </ul>
          </div>
        }
        <div class="tw-flex tw-flex-none tw-flex-col tw-h-[128px] sm:tw-h-[150px] tw-pt-4">
          <button csButton
            type="button"
            class="tw-w-full"
            [disabled]="!isSecondaryConditionSelected || ui.saveInProcess"
            [loading]="ui.saveInProcess && isSecondaryConditionSelected"
            (click)="handleContinueSecondaryConcern()"
          i18n="@@continue">Continue</button>
          <button csButton="muted"
            type="button"
            class="tw-w-full"
            [disabled]="ui.saveInProcess"
            [loading]="ui.saveInProcess && !isSecondaryConditionSelected"
            (click)="handleSkipSecondaryConcern()"
          i18n="@@skip">Skip</button>
        </div>
      } @else {
        <!-- Primary concerns list -->
        <div class="tw-min-h-0 tw-flex-1 tw-overflow-x-hidden tw-overflow-y-auto"
          [ngClass]="pageEnterAnimationClass">
          <ul class="tw-flex tw-flex-col tw-gap-4">
            @for (item of concernSections.TREATMENT; track item; let i = $index) {
              <li class="tw-flex tw-gap-2"
                (click)="handleAddPrimaryConcern(item)">
                <!-- Unchecked -->
                @if (!selectedConcerns.includes(item.key)) {
                  <div class="tw-m-[6px] tw-bg-gray-50 tw-border-2 tw-border-solid tw-border-gray-400 tw-rounded-[50%] tw-shrink-0"
                    [ngClass]="hairMultiConcernUI ? 'tw-w-5 tw-h-5' : 'tw-w-6 tw-h-6'">
                  </div>
                }
                @if (selectedConcerns.includes(item.key) && !hairMultiConcernUI) {
                  <div class="tw-w-6 tw-h-6 tw-m-[6px] tw-shrink-0 tw-bg-teal-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-animate-tick_pop">
                    <cs-icon class="tw-text-white tw-h-5 tw-w-5"
                      [iconName]="'done'"></cs-icon>
                  </div>
                }
                <!-- Checked (HAIR) -->
                @if (selectedConcerns.includes(item.key) && hairMultiConcernUI) {
                  <img class="tw-w-5 tw-h-5 tw-m-[6px] tw-shrink-0 tw-animate-tick_pop"
                    src="/assets/images/primary-concern-selected.svg"
                    alt="selected" />
                }
                <div class="tw-flex tw-flex-col">
                  <span class="tw-font-lexend tw-text-400 tw-leading-[29px] tw-text-gray-800 tw-font-semibold"
                    [class.tw-capitalize]="hairMultiConcernUI">{{item.key}}</span>
                  <span class="tw-font-body tw-text-200 tw-leading-[15px] tw-text-gray-400 tw-font-regular">{{item.description || ''}}</span>
                </div>
              </li>
            }
          </ul>
        </div>
        <div class="tw-flex tw-flex-none tw-flex-col tw-h-[128px] sm:tw-h-[150px] tw-pt-4">
          <button csButton
            type="button"
            class="tw-w-full"
            [loading]="ui.saveInProcess && selectedConcerns.length && !isNoConcernSelected"
            [disabled]="ui.saveInProcess || !selectedConcerns.length"
            (click)="handleShowSecondaryConcernsScreen()"
            i18n="@@next">Next
          </button>
          <button csButton="muted"
            type="button"
            class="tw-w-full"
            [loading]="ui.saveInProcess && selectedConcerns.length && isNoConcernSelected"
            [disabled]="ui.saveInProcess || !!selectedConcerns.length"
            (click)="handleSkipPrimaryConcern()"
            i18n="@@iDontHaveAnyConcern">I don't have any concern
          </button>
        </div>
      }
    }
  </div>
</div>
